import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import StickyHead from "../StickyHead";

const STATUS_LABEL = { ACTIVE: "정상", WITHDRAWN: "탈퇴" };
/* 회원 등급 — 가입하면 일반회원. 관리자는 포럼 · 라오나봇 관리자 권한을 받고,
   정회원은 라오나봇 무료 한도가 2,000원이다 (백엔드 app/sstoa/api/members.py · app/laona_projects.py) */
const GRADES = [
  ["ADMIN", "관리자"],
  ["REGULAR", "정회원"],
  ["BASIC", "일반회원"],
];
const GRADE_LABEL = Object.fromEntries(GRADES);

/* 백엔드가 UTC 로 기록하므로 표시할 때 KST 로 바꾼다 */
function when(iso) {
  if (!iso) return "—";
  const d = new Date(/[Zz+]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "short", timeStyle: "short" });
}

export default function MembersAdmin() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(0); // 등급을 바꾸는 중인 회원 id

  async function changeGrade(m, grade) {
    if (grade === m.grade) return;
    if (grade === "ADMIN" && !window.confirm(`${m.name}(${m.email}) 님을 관리자로 바꿀까요?\n포럼 · 라오나봇 관리자 권한(모듈 관리 · 모든 프로젝트 보기 등)을 갖게 됩니다.`)) return;
    if (m.grade === "ADMIN" && !window.confirm(`${m.name} 님의 관리자 권한을 거두고 ${GRADE_LABEL[grade]}으로 바꿀까요?`)) return;
    setSaving(m.id);
    setError("");
    try {
      const updated = await api.post(`/members/${m.id}/grade`, { grade });
      setRows((rs) => rs.map((r) => (r.id === m.id ? updated : r)));
      setCounts(await api.get("/members/counts"));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(0);
    }
  }

  async function load(status = filter, keyword = q) {
    setError("");
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (keyword.trim()) params.set("q", keyword.trim());
    const qs = params.toString();
    try {
      const [list, c] = await Promise.all([
        api.get(`/members${qs ? `?${qs}` : ""}`),
        api.get("/members/counts"),
      ]);
      setRows(list);
      setCounts(c);
    } catch (err) { setError(err.message); }
  }

  useEffect(() => {
    load("", "");
    // 최초 1회만. 이후 갱신은 필터·검색이 직접 호출한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">회원 관리</h1>
            <p className="sstoa-sub">홈페이지 가입 회원입니다. 개인정보 보호를 위해 회원 정보는 조회만 하고, 등급만 바꿀 수 있습니다.</p>
          </div>
        </div>

        {counts && (
          <div className="sstoa-stats" style={{ marginBottom: 16 }}>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{counts.total}</div><div className="sstoa-stat__label">전체</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{counts.active}</div><div className="sstoa-stat__label">정상</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{counts.verified}</div><div className="sstoa-stat__label">이메일 인증</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{counts.regular ?? 0}</div><div className="sstoa-stat__label">정회원</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{counts.admin ?? 0}</div><div className="sstoa-stat__label">관리자</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{counts.withdrawn}</div><div className="sstoa-stat__label">탈퇴</div></div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <button className={`sstoa-btn sstoa-btn--sm${filter === "" ? " sstoa-btn--primary" : ""}`}
                  onClick={() => { setFilter(""); load("", q); }}>전체</button>
          <button className={`sstoa-btn sstoa-btn--sm${filter === "ACTIVE" ? " sstoa-btn--primary" : ""}`}
                  onClick={() => { setFilter("ACTIVE"); load("ACTIVE", q); }}>정상</button>
          <button className={`sstoa-btn sstoa-btn--sm${filter === "WITHDRAWN" ? " sstoa-btn--primary" : ""}`}
                  onClick={() => { setFilter("WITHDRAWN"); load("WITHDRAWN", q); }}>탈퇴</button>
          <form onSubmit={(e) => { e.preventDefault(); load(filter, q); }} style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            <input className="sstoa-input" style={{ width: 200 }} placeholder="이메일 · 이름 검색"
                   value={q} onChange={(e) => setQ(e.target.value)} />
            <button type="submit" className="sstoa-btn sstoa-btn--sm">검색</button>
          </form>
        </div>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">회원이 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>상태</th><th>회원</th><th>등급</th><th>회사</th><th>연락처</th><th>가입</th><th>최근 로그인</th></tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className={`sstoa-tag ${m.status === "ACTIVE" ? "" : "sstoa-tag--gray"}`}>
                      {STATUS_LABEL[m.status] || m.status}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {m.name}
                    <div style={{ fontWeight: 400, fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
                      {m.email}{m.email_verified ? " · 인증됨" : " · 미인증"}
                    </div>
                  </td>
                  <td>
                    <select className="sstoa-select" style={{ width: 112, padding: "5px 8px" }} aria-label={`${m.name} 등급`}
                            value={m.grade || "BASIC"} disabled={saving === m.id || m.status !== "ACTIVE"}
                            onChange={(e) => changeGrade(m, e.target.value)}>
                      {GRADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {m.env_admin && m.grade !== "ADMIN" && (
                      <div style={{ fontSize: 11.5, color: "var(--s-fg-mute)", marginTop: 3 }} title="서버 설정(LAONA_ADMIN_EMAILS)으로 정한 라오나봇 관리자입니다">
                        라오나봇 관리자(설정)
                      </div>
                    )}
                  </td>
                  <td>{m.company || "—"}</td>
                  <td>{m.phone || "—"}</td>
                  <td>{when(m.created_at)}</td>
                  <td>{when(m.last_login_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
